// src/pages/outsourcing_management/AwaitingEnquiryTable.tsx
//
// The buyer's pool (EE-253 P2b): every line of a submitted requisition that
// no live Inquiry carries, from every order. The buyer picks lines of one
// type and builds a Draft Inquiry from them, or adds them to an Inquiry of
// that type that still collects lines (Draft, or Approved and not sent).
// Lines can be grouped by the partner their requester suggested, by type,
// or by requisition.
import {
  FunctionComponent,
  ReactNode,
  useContext,
  useMemo,
  useState,
} from "react";
import { useQuery } from "react-query";
import { useHistory } from "react-router";
import {
  Alert,
  Box,
  Button,
  Checkbox,
  Chip,
  FormControl,
  InputLabel,
  LinearProgress,
  MenuItem,
  Paper,
  Select,
  Skeleton,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TablePagination,
  TableRow,
  Typography,
} from "@mui/material";
import AppStateContext from "contexts/AppStateContext";
import { useToast } from "contexts/ToastContext";
import {
  AwaitingLineType,
  MatchingDraftType,
} from "util/network/RequisitionPool";
import { apiGetInquiries, apiGetInquiry } from "util/network/Inquiries";
import { hasLines, InquiryStatusEnum, InquiryType } from "types/InquiryTypes";
import { reformatDate } from "util/DateUtil";
import { poolKeys, useAwaitingEnquiry } from "hooks/useRequisitionPool";
import DashboardTableHeadCell, {
  DashboardTableRefreshCell,
} from "components/Dashboard/DashboardTableHeadCell";
import { SKELETON_ROW_HEIGHT } from "components/Dashboard/dashboardTable";
import EnquiryComposeDialog, { poolLinesOf } from "./EnquiryComposeDialog";
import RequisitionPartPreview from "./requisitions/RequisitionPartPreview";

type PropsType = {
  searchValue: string;
  onCreated: (inquiryId: number) => void;
};

type ColumnType = {
  id: string;
  label: string;
  align?: "left" | "right";
  // Share of the table width. Without it the long, spaceless file names
  // get squeezed to a few characters a line.
  width?: string;
  // What the column sorts by - the whole pool is loaded, so sorting is local.
  sortValue: (row: AwaitingLineType) => string | number;
  render: (row: AwaitingLineType, token: string) => ReactNode;
};

// Second, grey line under a cell's main value - keeps related facts in one
// column instead of a column each.
const Caption: FunctionComponent<{ children: ReactNode }> = ({ children }) => (
  <Typography variant="caption" color="text.secondary" display="block">
    {children}
  </Typography>
);

const COLUMNS: ColumnType[] = [
  {
    id: "requisition",
    label: "Requisition",
    width: "13%",
    sortValue: (row) =>
      `${row.requisition_no} ${String(row.line_no ?? 0).padStart(4, "0")}`,
    render: (row) => (
      <>
        <Box component="span" sx={{ whiteSpace: "nowrap" }}>
          {row.requisition_no} / L{row.line_no ?? "?"}
        </Box>
        {row.sales_order_no ? <Caption>SO {row.sales_order_no}</Caption> : null}
      </>
    ),
  },
  {
    id: "type",
    label: "Type",
    width: "9%",
    sortValue: (row) => row.type_label ?? "",
    render: (row) => row.type_label ?? row.type_code ?? "-",
  },
  {
    id: "part",
    label: "Part",
    width: "26%",
    sortValue: (row) => row.part_name ?? "",
    render: (row, token) =>
      row.part_name ? (
        <Box sx={{ display: "flex", alignItems: "center", gap: 1.5 }}>
          {row.model_id ? (
            <RequisitionPartPreview
              token={token}
              modelId={row.model_id}
              size={36}
            />
          ) : null}
          {/* File names have no spaces - let them break anywhere. */}
          <Box component="span" sx={{ minWidth: 0, overflowWrap: "anywhere" }}>
            {row.part_name}
          </Box>
        </Box>
      ) : (
        "-"
      ),
  },
  {
    id: "what",
    label: "Material / Service",
    width: "22%",
    sortValue: (row) => row.what,
    // For a part-based line "what" is the part itself - already in Part.
    render: (row) => (
      <>
        <Box component="span" sx={{ overflowWrap: "anywhere" }}>
          {row.what === row.part_name ? "-" : row.what}
        </Box>
        {row.spec ? <Caption>{row.spec}</Caption> : null}
      </>
    ),
  },
  {
    id: "required",
    label: "Required",
    align: "right",
    width: "10%",
    sortValue: (row) => row.required_amount ?? 0,
    render: (row) => (
      <>
        <Box
          component="span"
          sx={{ whiteSpace: "nowrap", fontVariantNumeric: "tabular-nums" }}
        >
          {row.required || "-"}
        </Box>
        {row.required_by_date ? (
          <Caption>by {reformatDate(row.required_by_date)}</Caption>
        ) : null}
      </>
    ),
  },
  {
    id: "supplier",
    label: "Suggested Supplier",
    width: "10%",
    sortValue: (row) => row.supplier_name ?? "",
    render: (row) => row.supplier_name ?? "-",
  },
  {
    id: "requester",
    label: "Requester",
    width: "10%",
    sortValue: (row) => row.requester_name ?? "",
    render: (row) => row.requester_name ?? "-",
  },
];

// Checkbox in front, refresh control at the end.
const COLSPAN = COLUMNS.length + 2;

const inquiryNo = (id: number) => `INQ-${String(id).padStart(6, "0")}`;

const stateLabel = (state: string | null) =>
  state === InquiryStatusEnum.APPROVED ? "Approved" : "Draft";

// "Add to INQ-… (Draft, partner)" - one click puts the line(s) on that
// Inquiry, through the same dialog as Add to Draft Inquiry.
// "Add to INQ-…" chips on lines an open Inquiry would take (spec 6.6). Off
// for now: the "Add to open Inquiry" menu covers it with less on screen.
const SHOW_MATCH_CHIPS = false;

const MatchChip: FunctionComponent<{
  draft: MatchingDraftType;
  label?: string;
  onAdd: () => void;
}> = ({ draft, label = "Add to", onAdd }) => (
  <Chip
    size="small"
    color="info"
    variant="outlined"
    label={`${label} ${inquiryNo(draft.id)} (${[
      stateLabel(draft.state),
      draft.partner_name,
    ]
      .filter(Boolean)
      .join(", ")})`}
    onClick={(e) => {
      // The row opens its requisition on click.
      e.stopPropagation();
      onAdd();
    }}
    data-testid={`awaiting-match-${draft.id}`}
    sx={{ mt: 0.5, maxWidth: "100%" }}
  />
);

const AwaitingEnquiryTable: FunctionComponent<PropsType> = ({
  searchValue,
  onCreated,
}) => {
  const { token } = useContext(AppStateContext);
  const { addToast } = useToast();
  const history = useHistory();
  const {
    data: rows = [],
    isLoading,
    isFetching,
    isError,
    error,
    refetch,
  } = useAwaitingEnquiry(token);
  // Inquiries that can still take lines: Draft, or Approved and not yet
  // sent to a supplier.
  const { data: drafts = [] } = useQuery(
    poolKeys.drafts(),
    () =>
      apiGetInquiries(
        token,
        1,
        100,
        "id",
        "desc",
        `${InquiryStatusEnum.DRAFT} ${InquiryStatusEnum.APPROVED}`
      ).then((res) =>
        (res.items ?? []).filter((inquiry: InquiryType) => !inquiry.sentAt)
      ),
    { enabled: !!token }
  );
  const [groupBy, setGroupBy] = useState<GroupBy>("partner");
  const grouped = groupBy !== "none";
  const groupKey = (row: AwaitingLineType) => groupOf(groupBy, row).key;
  const [typeFilter, setTypeFilter] = useState("");
  const [selected, setSelected] = useState<number[]>([]);
  // Earliest needed first, as the backend sends it, until a header is clicked.
  const [sorting, setSorting] = useState<{ id: string; desc: boolean }>();
  const [page, setPage] = useState(0);
  const [pageSize, setPageSize] = useState(25);
  const [compose, setCompose] = useState<{
    lines: AwaitingLineType[];
    inquiryId?: number;
    partnerId?: number | null;
    deadline?: string | null;
  }>();

  const types = useMemo(
    () =>
      Array.from(
        new Map(
          rows.map((row) => [
            row.type_code ?? "",
            row.type_label ?? row.type_code ?? "",
          ])
        )
      ),
    [rows]
  );
  const terms = searchValue.toLowerCase().split(/\s+/).filter(Boolean);
  const visible = rows.filter(
    (row) =>
      (!typeFilter || row.type_code === typeFilter) &&
      terms.every((term) =>
        [
          row.requisition_no,
          row.sales_order_no,
          row.part_name,
          row.what,
          row.spec,
          row.supplier_name,
          row.requester_name,
        ]
          .join(" ")
          .toLowerCase()
          .includes(term)
      )
  );
  const sortColumn = COLUMNS.find((column) => column.id === sorting?.id);
  let sorted = sortColumn
    ? [...visible].sort((x, y) => {
        const a = sortColumn.sortValue(x);
        const b = sortColumn.sortValue(y);
        const order =
          typeof a === "number" && typeof b === "number"
            ? a - b
            : String(a).localeCompare(String(b));
        return sorting?.desc ? -order : order;
      })
    : visible;
  // Grouped: each group where its most urgent line would be, its lines
  // together in the order above.
  if (grouped) {
    const first = new Map<string, number>();
    sorted.forEach((row, index) => {
      if (!first.has(groupKey(row))) first.set(groupKey(row), index);
    });
    sorted = [...sorted].sort(
      (x, y) => first.get(groupKey(x))! - first.get(groupKey(y))!
    );
  }
  const lastPage = Math.max(0, Math.ceil(sorted.length / pageSize) - 1);
  const shownPage = Math.min(page, lastPage);
  const pageRows = sorted.slice(
    shownPage * pageSize,
    (shownPage + 1) * pageSize
  );
  const sortBy = (id: string) =>
    setSorting(
      sorting?.id === id ? { id, desc: !sorting.desc } : { id, desc: false }
    );

  // A line can drop out of the pool under the selection (someone else
  // composed it); only what is still there counts.
  const chosen = rows.filter((row) =>
    selected.includes(row.requisition_item_id)
  );
  const mixed = new Set(chosen.map((row) => row.inquiry_type)).size > 1;
  const sameTypeDrafts = drafts.filter(
    (draft: InquiryType) =>
      chosen.length > 0 && draft.type === chosen[0].inquiry_type
  );

  const toggle = (id: number) =>
    setSelected(
      selected.includes(id)
        ? selected.filter((s) => s !== id)
        : [...selected, id]
    );
  const allVisible =
    visible.length > 0 &&
    visible.every((row) => selected.includes(row.requisition_item_id));

  const visibleIn = (key: string) =>
    visible.filter((row) => groupKey(row) === key);
  const selectGroup = (key: string) => {
    const ids = visibleIn(key).map((row) => row.requisition_item_id);
    const all = ids.every((id) => selected.includes(id));
    setSelected(
      all
        ? selected.filter((id) => !ids.includes(id))
        : Array.from(new Set([...selected, ...ids]))
    );
  };
  // The open Inquiries every line of a group matches - "add the group".
  const groupMatches = (key: string) => {
    const lines = visibleIn(key);
    return lines[0].matching_drafts.filter((draft) =>
      lines.every((line) =>
        line.matching_drafts.some((match) => match.id === draft.id)
      )
    );
  };

  const addToDraft = (
    inquiryId: number,
    adding: AwaitingLineType[] = chosen
  ) =>
    // The Draft's current lines plus the chosen ones, so the dialog shows the
    // whole Inquiry the buyer is about to save.
    apiGetInquiry(token, inquiryId)
      .then((draft) => {
        const existing = hasLines(draft) ? poolLinesOf(draft, adding[0]) : [];
        const have = new Set(existing.map((line) => line.requisition_item_id));
        setCompose({
          lines: [
            ...existing,
            ...adding.filter((row) => !have.has(row.requisition_item_id)),
          ],
          inquiryId,
          partnerId: draft.partnerId,
          deadline: draft.deliveryDate,
        });
      })
      .catch((err) =>
        addToast({
          type: "error",
          message: `Could not open the Draft: ${err}`,
          keep: true,
        })
      );

  const closeCompose = (savedId?: number) => {
    setCompose(undefined);
    if (savedId === undefined) return;
    setSelected([]);
    addToast({
      type: "success",
      message: `Draft Inquiry INQ-${String(savedId).padStart(6, "0")} saved`,
    });
    onCreated(savedId);
  };

  return (
    <>
      <EnquiryComposeDialog
        open={compose !== undefined}
        lines={compose?.lines ?? []}
        inquiryId={compose?.inquiryId}
        partnerId={compose?.partnerId}
        deadline={compose?.deadline}
        onClose={closeCompose}
      />
      <Box
        sx={{
          display: "flex",
          gap: 2,
          alignItems: "center",
          flexWrap: "wrap",
          mb: 2,
        }}
      >
        <FormControl size="small" sx={{ minWidth: 200 }}>
          <InputLabel>Type</InputLabel>
          <Select
            label="Type"
            value={typeFilter}
            onChange={(e) => setTypeFilter(e.target.value)}
          >
            <MenuItem value="">All types</MenuItem>
            {types.map(([code, label]) => (
              <MenuItem key={code} value={code}>
                {label}
              </MenuItem>
            ))}
          </Select>
        </FormControl>
        <FormControl size="small" sx={{ minWidth: 160 }}>
          <InputLabel>Group by</InputLabel>
          <Select
            label="Group by"
            value={groupBy}
            onChange={(e) => setGroupBy(e.target.value as GroupBy)}
            data-testid="awaiting-group-by"
          >
            <MenuItem value="none">No grouping</MenuItem>
            <MenuItem value="partner">Partner</MenuItem>
            <MenuItem value="type">Type</MenuItem>
            <MenuItem value="requisition">Requisition</MenuItem>
          </Select>
        </FormControl>
        <Typography variant="body2" color="text.secondary" sx={{ flex: 1 }}>
          {chosen.length
            ? `${chosen.length} line(s) selected`
            : `${visible.length} line(s) waiting`}
        </Typography>
        {sameTypeDrafts.length > 0 && !mixed && (
          <FormControl size="small" sx={{ minWidth: 240 }}>
            <InputLabel>Add to open Inquiry</InputLabel>
            <Select
              label="Add to open Inquiry"
              value=""
              onChange={(e) => addToDraft(Number(e.target.value))}
            >
              {sameTypeDrafts.map((draft: InquiryType) => (
                <MenuItem key={draft.id} value={draft.id}>
                  {draft.inquiryNo ?? `#${draft.id}`} ·{" "}
                  {draft.state === InquiryStatusEnum.APPROVED
                    ? "Approved"
                    : "Draft"}{" "}
                  · {draft.partner?.info?.company ?? "no supplier"}
                </MenuItem>
              ))}
            </Select>
          </FormControl>
        )}
        <Button
          variant="contained"
          disabled={!chosen.length || mixed}
          onClick={() => setCompose({ lines: chosen })}
          data-testid="awaiting-create-enquiry"
        >
          Create Inquiry
        </Button>
      </Box>
      {mixed && (
        <Alert severity="warning" sx={{ mb: 2 }}>
          Select lines of the same type - one Inquiry is one type, one supplier,
          one PO.
        </Alert>
      )}
      {isError && (
        <Alert severity="error" sx={{ mb: 2 }}>
          Could not load the lines waiting for an inquiry: {String(error)}
        </Alert>
      )}
      <Paper sx={{ width: "100%" }}>
        {isFetching ? <LinearProgress /> : null}
        <TableContainer>
          <Table data-testid="awaiting-table">
            <TableHead sx={{ background: "var(--header)" }}>
              <TableRow>
                <TableCell padding="checkbox">
                  <Checkbox
                    sx={{
                      color: "var(--white)",
                      "&.Mui-checked, &.MuiCheckbox-indeterminate": {
                        color: "var(--white)",
                      },
                    }}
                    checked={allVisible}
                    indeterminate={
                      !allVisible &&
                      visible.some((r) =>
                        selected.includes(r.requisition_item_id)
                      )
                    }
                    onChange={() =>
                      setSelected(
                        allVisible
                          ? []
                          : Array.from(
                              new Set([
                                ...selected,
                                ...visible.map((r) => r.requisition_item_id),
                              ])
                            )
                      )
                    }
                  />
                </TableCell>
                {COLUMNS.map((column) => (
                  <DashboardTableHeadCell
                    key={column.id}
                    align={column.align}
                    onSort={() => sortBy(column.id)}
                    sortActive={sorting?.id === column.id}
                    sortDirection={
                      sorting?.id === column.id && sorting.desc ? "desc" : "asc"
                    }
                  >
                    {column.label}
                  </DashboardTableHeadCell>
                ))}
                <DashboardTableRefreshCell
                  onRefresh={() => refetch()}
                  disabled={isFetching}
                />
              </TableRow>
            </TableHead>
            <TableBody>
              {isLoading ? (
                Array.from({ length: 5 }).map((_, index) => (
                  <TableRow key={index}>
                    <TableCell colSpan={COLSPAN}>
                      <Skeleton
                        variant="rectangular"
                        height={SKELETON_ROW_HEIGHT}
                      />
                    </TableCell>
                  </TableRow>
                ))
              ) : pageRows.length === 0 ? (
                <TableRow>
                  <TableCell colSpan={COLSPAN} style={{ textAlign: "center" }}>
                    No lines waiting for an Inquiry
                  </TableCell>
                </TableRow>
              ) : (
                pageRows.flatMap((row, index) => [
                  ...(grouped &&
                  (index === 0 ||
                    groupKey(pageRows[index - 1]) !== groupKey(row))
                    ? [
                        <GroupRow
                          key={`group-${groupKey(row)}`}
                          group={groupOf(groupBy, row)}
                          detail={(line) =>
                            groupBy === "type"
                              ? line.supplier_name ?? "No suggested partner"
                              : line.type_label ?? line.type_code ?? ""
                          }
                          lines={visibleIn(groupKey(row))}
                          selected={selected}
                          matches={
                            SHOW_MATCH_CHIPS ? groupMatches(groupKey(row)) : []
                          }
                          onSelect={() => selectGroup(groupKey(row))}
                          onAdd={(inquiryId) =>
                            addToDraft(inquiryId, visibleIn(groupKey(row)))
                          }
                        />,
                      ]
                    : []),
                  <TableRow
                    key={row.key}
                    hover
                    selected={selected.includes(row.requisition_item_id)}
                    onClick={() =>
                      history.push(`/requisitions/${row.requisition_id}`)
                    }
                    sx={{ cursor: "pointer" }}
                    data-testid={`awaiting-row-${row.requisition_item_id}`}
                  >
                    {/* The checkbox cell selects; the rest of the row opens the requisition. */}
                    <TableCell
                      padding="checkbox"
                      onClick={(e) => {
                        e.stopPropagation();
                        toggle(row.requisition_item_id);
                      }}
                    >
                      <Checkbox
                        checked={selected.includes(row.requisition_item_id)}
                      />
                    </TableCell>
                    {COLUMNS.map((column) => (
                      <TableCell
                        key={column.id}
                        align={column.align}
                        sx={{ width: column.width }}
                      >
                        {column.render(row, token)}
                        {SHOW_MATCH_CHIPS &&
                        column.id === "requisition" &&
                        !grouped
                          ? row.matching_drafts.map((draft) => (
                              <MatchChip
                                key={draft.id}
                                draft={draft}
                                onAdd={() => addToDraft(draft.id, [row])}
                              />
                            ))
                          : null}
                      </TableCell>
                    ))}
                    <TableCell />
                  </TableRow>,
                ])
              )}
            </TableBody>
          </Table>
        </TableContainer>
        <TablePagination
          rowsPerPageOptions={[10, 25, 50, 100]}
          component="div"
          count={sorted.length}
          rowsPerPage={pageSize}
          page={shownPage}
          onPageChange={(_: unknown, newPage: number) => setPage(newPage)}
          onRowsPerPageChange={(e: React.ChangeEvent<HTMLInputElement>) => {
            setPageSize(+e.target.value);
            setPage(0);
          }}
        />
      </Paper>
    </>
  );
};

type GroupBy = "none" | "partner" | "type" | "requisition";

// Partner groups come from the server (the requester's suggested partner);
// type and requisition groups are the line's own.
const groupOf = (groupBy: GroupBy, row: AwaitingLineType) =>
  groupBy === "type"
    ? {
        key: `type:${row.type_code ?? "-"}`,
        label: row.type_label ?? row.type_code ?? "No type",
      }
    : groupBy === "requisition"
    ? {
        key: `requisition:${row.requisition_id}`,
        label: row.sales_order_no
          ? `${row.requisition_no} · SO ${row.sales_order_no}`
          : row.requisition_no,
      }
    : { key: row.group_key, label: row.group_label };

// The head of one group: what it is, how many lines, select them all, and
// the open Inquiries the whole group would join.
const GroupRow: FunctionComponent<{
  group: { key: string; label: string };
  // What the group's lines differ in: their types, or their partners.
  detail: (line: AwaitingLineType) => string;
  lines: AwaitingLineType[];
  selected: number[];
  matches: MatchingDraftType[];
  onSelect: () => void;
  onAdd: (inquiryId: number) => void;
}> = ({ group, detail, lines, selected, matches, onSelect, onAdd }) => {
  const picked = lines.filter((line) =>
    selected.includes(line.requisition_item_id)
  ).length;
  return (
    <TableRow
      sx={{ background: "var(--background-secondary, rgba(0,0,0,0.04))" }}
      data-testid={`awaiting-group-${group.key}`}
    >
      <TableCell padding="checkbox">
        <Checkbox
          checked={picked === lines.length}
          indeterminate={picked > 0 && picked < lines.length}
          onChange={onSelect}
          inputProps={{ "aria-label": "Select group" }}
        />
      </TableCell>
      <TableCell colSpan={COLSPAN - 1}>
        <Box
          sx={{
            display: "flex",
            alignItems: "center",
            gap: 1,
            flexWrap: "wrap",
          }}
        >
          <Typography variant="subtitle2" component="span">
            {group.label}
          </Typography>
          <Typography variant="caption" color="text.secondary">
            {Array.from(new Set(lines.map(detail))).join(", ")}{" "}
            · {lines.length} line(s)
          </Typography>
          {matches.map((draft) => (
            <MatchChip
              key={draft.id}
              draft={draft}
              label="Add group to"
              onAdd={() => onAdd(draft.id)}
            />
          ))}
        </Box>
      </TableCell>
    </TableRow>
  );
};

export default AwaitingEnquiryTable;
